import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast, Toast, ToastVariant } from '@hungryroot/components-toast';
import {
  StatusCode,
  deleteFavoriteProduct,
  getFavoriteProductsPath,
  type CustomerFavoriteProducts,
} from '@hungryroot/rest';
import { getFavoritesCookbookQueryKey } from '@hungryroot/rest-hooks';
import {
  type AccessedFrom,
  type FeedInteractionFields,
  type Location,
} from '@hungryroot/segment';
import { Warning } from '@hungryroot/ui-kit';
import { trackFavorite } from '../segment/track-favorite';

/**
 * Spreads {@link FeedInteractionFields} so a call site inside a marketing
 * carousel can supply `container` / `carousel_id` / `carousel_name` /
 * `horizontal_position` per the Exp 242 nav-IA logging spec.
 */
type FavoriteProductSegment = FeedInteractionFields & {
  location: Location | null;
  orderId?: number;
  pageName?: string;
  accessedFrom?: AccessedFrom;
  conversationId?: string;
  searchId?: string;
  resultPosition?: number;
};

interface FavoriteProductData {
  customerId: number;
  productId: number;
  productName: string;
  segment?: FavoriteProductSegment;
}

type Variables = { data: FavoriteProductData };

interface MutateContext {
  previousFavoriteProducts: CustomerFavoriteProducts | undefined;
}

function useDeleteFavoriteProductMutation(
  onSuccess?: (variables: Variables) => void
) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ data }: Variables) => {
      const res = await deleteFavoriteProduct({
        customerId: data.customerId,
        productId: data.productId,
      });
      if (res.status !== StatusCode.SuccessNoContent) {
        throw new Error(JSON.stringify(res.data, null, 2));
      }
      return res.data;
    },
    onMutate: async ({ data }: Variables): Promise<MutateContext> => {
      const { customerId, productId: deletedProductId } = data;

      await queryClient.cancelQueries({
        queryKey: [getFavoriteProductsPath({ customerId })],
      });

      const previousFavoriteProducts: CustomerFavoriteProducts | undefined =
        queryClient.getQueryData([getFavoriteProductsPath({ customerId })]);

      // Only update the cached list when it actually exists — some surfaces
      // (e.g. the owner /mycookbook/ page) never fetch it. Unconditionally
      // writing the entry here used to store `{ favorite_products: undefined }`,
      // which made every subsequent unfavorite throw in onMutate before its
      // DELETE request was sent (first worked, later ones failed until reload).
      const currentFavoriteProducts =
        previousFavoriteProducts?.favorite_products;
      if (currentFavoriteProducts != null) {
        queryClient.setQueryData<CustomerFavoriteProducts>(
          [getFavoriteProductsPath({ customerId })],
          {
            favorite_products: currentFavoriteProducts.filter(
              (obj) => obj.product_id !== deletedProductId
            ),
          }
        );
      }

      return { previousFavoriteProducts };
    },
    onError: (_err, { data }: Variables, context?: MutateContext) => {
      const { customerId } = data;

      toast(
        <Toast variant={ToastVariant.Critical}>
          <Warning />
          Unable to complete action. Please try again.
        </Toast>
      );

      queryClient.setQueryData(
        [getFavoriteProductsPath({ customerId })],
        context?.previousFavoriteProducts
      );
    },
    onSettled: (_data, _err, { data }: Variables) => {
      const filterParam = 'is_favorite=1';

      queryClient.invalidateQueries({
        queryKey: [getFavoriteProductsPath({ customerId: data.customerId })],
      });

      // Keep the owner-view favorites cookbook in sync with the unfavorite.
      queryClient.invalidateQueries({
        queryKey: getFavoritesCookbookQueryKey(),
      });

      // Invalidate product filter queries that include favorites
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === 'ProductsV2' &&
          typeof query.queryKey[1] === 'string' &&
          query.queryKey[1].includes(filterParam),
      });
    },
    onSuccess: onSuccess
      ? (_data, variables) => onSuccess(variables)
      : undefined,
  });
}

/**
 * Removes a product from favorites. Fires the default `Product Interaction`
 * (unsaved) Segment event via `trackFavorite`, but only when the mutation's
 * `data.segment` is provided; if `segment` is omitted, no event is sent. Use
 * `useDeleteFavoriteProductWithoutTracking` on surfaces (e.g. the Exp 242 home
 * feed) that emit their own interaction event.
 */
export function useDeleteFavoriteProduct() {
  return useDeleteFavoriteProductMutation(({ data }) => {
    if (data.segment === undefined) return;

    // Spread rather than enumerate: `segment` is `FeedInteractionFields` plus
    // the favorite-specific keys, and `trackFavorite` accepts exactly that
    // shape, so new spec columns need no edit here.
    trackFavorite({
      ...data.segment,
      id: data.productId,
      name: data.productName,
      category: 'products',
      state: false,
    });
  });
}

/**
 * Removes a product from favorites without firing the default
 * `Product Interaction` event. For surfaces (e.g. the Exp 242 home feed) that
 * emit their own interaction analytics.
 */
export function useDeleteFavoriteProductWithoutTracking() {
  return useDeleteFavoriteProductMutation();
}
